import { useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, InputAdornment, LinearProgress, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { planRegistrationCopy } from "./registrationCopy";

const emptySelection = () => ({ sourceId: "", itemIds: [], targetIds: [] });
const wrapping = { overflowWrap: "anywhere", minWidth: 0, whiteSpace: "normal" };
const listHeight = { xs: 220, sm: 330 };
const searchAdornment = <InputAdornment position="start"><SearchIcon sx={{ fontSize: 19, color: "text.secondary" }} /></InputAdornment>;

function CopyChoice({ row, checked, disabled, onChange }) {
  return <FormControlLabel
    sx={(theme) => ({
      m: 0, width: "100%", minHeight: 56, boxSizing: "border-box", px: "8px", py: "6px",
      alignItems: "center", borderBottom: "1px solid", borderColor: "divider",
      borderLeft: "3px solid", borderLeftColor: checked ? "primary.main" : "transparent",
      bgcolor: checked ? alpha(theme.palette.primary.main, 0.07) : "transparent",
      transition: "background-color 120ms", cursor: disabled ? "default" : "pointer",
      "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
      "& .MuiCheckbox-root.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
      "& .MuiFormControlLabel-label": { ...wrapping, flex: 1, pr: 1 },
      "& .MuiCheckbox-root": { p: 1, mr: 0.5, "& svg": { fontSize: 22 } },
    })}
    control={<Checkbox disabled={disabled} checked={checked} onChange={onChange} inputProps={{ "aria-label": row.label }} />}
    label={<Box>
      <Typography component="span" sx={{ display: "block", fontSize: 14, lineHeight: 1.55, fontWeight: 400, color: "text.primary", ...wrapping }}>{row.title || row.label}</Typography>
      {row.detail && <Typography component="span" sx={{ display: "block", fontSize: 12, lineHeight: 1.5, color: "text.secondary", mt: 0.25, ...wrapping }}>{row.detail}</Typography>}
    </Box>} />;
}

export default function BulkCopyButton({ kind, label, loadSnapshot, saveCopy, onCopied, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState([]);
  const [selection, setSelection] = useState(emptySelection);
  const [phase, setPhase] = useState("select");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [targetSearch, setTargetSearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const pending = useRef(false);
  const source = snapshot.find((row) => row.id === selection.sourceId);
  const itemName = kind === "machine" ? "평가 대상" : "위험요인";
  const visibleItems = (source?.items || []).filter(row => row.label.toLowerCase().includes(itemSearch.trim().toLowerCase()));
  const visibleTargets = source ? snapshot.filter(row => row.id !== source.id && row.label.toLowerCase().includes(targetSearch.trim().toLowerCase())) : [];
  const visibleItemsSelected = visibleItems.filter(row => selection.itemIds.includes(row.id)).length;
  const visibleTargetsSelected = visibleTargets.filter(row => selection.targetIds.includes(row.id)).length;
  const selectVisible = (key, rows, checked) => change({ ...selection, [key]: checked
    ? [...new Set([...selection[key], ...rows.map(row => row.id)])]
    : selection[key].filter(id => !rows.some(row => row.id === id)) });
  let plan;
  try { plan = planRegistrationCopy(kind, snapshot, selection); } catch { /* Incomplete selection. */ }

  const load = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try { setSnapshot(await loadSnapshot()); }
    catch { setError("데이터를 불러오지 못했습니다. 다시 시도하세요."); }
    finally { pending.current = false; setBusy(false); }
  };
  const start = () => {
    if (pending.current) return;
    setSnapshot([]);
    setSelection(emptySelection());
    setPhase("select");
    setResult(null);
    setTargetSearch("");
    setItemSearch("");
    setOpen(true);
    load();
  };
  const change = (next) => { setSelection(next); setError(""); };
  const confirm = async () => {
    if (pending.current || !plan) return;
    pending.current = true;
    setBusy(true);
    setError("");
    let completed;
    try {
      const fresh = await loadSnapshot();
      setSnapshot(fresh);
      const current = planRegistrationCopy(kind, fresh, selection);
      // Never silently expand a preview when another user changes the links.
      if (JSON.stringify(current.payload) !== JSON.stringify(plan.payload)) {
        setError("등록 내용이 바뀌었습니다. 추가할 내용을 다시 확인해 주세요.");
        return;
      }
      if (current.added) await saveCopy(current.payload);
      completed = current;
      setResult(current);
      setPhase("done");
    } catch (err) {
      setError(err?.message || "복사하지 못했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
    if (completed) {
      try { await onCopied?.(); }
      catch { setError("복사는 완료되었으나 목록을 갱신하지 못했습니다. 목록을 새로고침하세요."); }
    }
  };

  return <>
    <Button variant="outlined" startIcon={<ContentCopyIcon />} disabled={disabled} onClick={start}>{label}</Button>
    <Dialog open={open} onClose={() => !pending.current && setOpen(false)} fullWidth maxWidth={false}
      aria-labelledby={`bulk-copy-${kind}-title`}
      PaperProps={{ sx: { m: { xs: 1, sm: 3 }, width: "calc(100% - 32px)", maxWidth: 1040, maxHeight: "calc(100dvh - 32px)", borderRadius: "8px", letterSpacing: 0,
        "& .MuiButton-root": { minHeight: { xs: 44, sm: 40 }, boxShadow: "none", borderRadius: "6px", fontSize: 14, whiteSpace: "nowrap" },
        "& .MuiInputBase-root": { fontSize: 14 }, "& .MuiOutlinedInput-root": { borderRadius: "6px" } } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", px: { xs: "16px", sm: "24px" }, py: "16px", borderBottom: "1px solid", borderColor: "divider" }}>
        <ContentCopyIcon sx={{ fontSize: 22, color: "primary.main", flexShrink: 0 }} />
        <DialogTitle id={`bulk-copy-${kind}-title`} sx={{ p: 0, flex: 1, fontSize: 18, fontWeight: 600, ...wrapping }}>{label}</DialogTitle>
        <Tooltip title="닫기"><span><IconButton aria-label="복사 창 닫기" disabled={busy} onClick={() => setOpen(false)} size="small"><CloseIcon /></IconButton></span></Tooltip>
      </Box>
      <Box sx={{ height: 3, flexShrink: 0 }}>{busy && <LinearProgress aria-label="복사 처리 중" sx={{ height: 3 }} />}</Box>
      <DialogContent sx={{ minWidth: 0, px: { xs: "16px", sm: "24px" }, py: "20px" }}>
        <Stack spacing="20px">
          {error && <Alert severity="error" sx={wrapping}>{error}</Alert>}
          {phase === "done" ? <Box role="status" sx={{ py: 5, textAlign: "center" }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 44, color: "success.main", mb: 1.5 }} />
            <Typography component="h3" sx={{ fontSize: 20, fontWeight: 600, mb: 1 }}>복사 완료</Typography>
            <Typography color="text.secondary" sx={wrapping}>{result.added}건을 추가했습니다. 이미 등록된 {result.skipped}건은 그대로 유지했습니다.</Typography>
          </Box> : <>
            {phase === "select" ? <>
              <Box sx={{ minWidth: 0 }}>
              <Typography component="h3" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary", mb: "8px" }}>가져올 설비·구역</Typography>
              <Autocomplete options={snapshot.filter((row) => row.items.length)} value={source || null}
                disabled={busy} getOptionLabel={(row) => row.label} isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="복사할 항목이 있는 설비·구역이 없습니다"
                onChange={(_, row) => { change({ sourceId: row?.id || "", itemIds: [], targetIds: [] }); setItemSearch(""); setTargetSearch(""); }}
                renderInput={(params) => <TextField {...params} placeholder="설비·구역을 선택하세요" inputProps={{ ...params.inputProps, "aria-label": kind === "machine" ? "가져올 설비·구역" : "가져올 설비·구역과 평가 대상" }} />}
                renderOption={(props, row) => <li {...props} key={row.id} style={{ ...wrapping, minHeight: 48 }}><Box><Typography sx={{ fontSize: 14 }}>{row.title || row.label}</Typography>{row.detail && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{row.detail}</Typography>}</Box></li>} />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1.2fr) minmax(0, 1fr)" }, gap: { xs: "20px", sm: "24px" }, minWidth: 0 }}>
                {[{ key: "itemIds", heading: `복사할 ${itemName}`, rows: visibleItems, selected: visibleItemsSelected, search: itemSearch, setSearch: setItemSearch, searchLabel: `복사할 ${itemName} 검색`, allLabel: "항목 전체 선택", empty: "검색 결과가 없습니다." },
                  { key: "targetIds", heading: "받을 설비·구역", rows: visibleTargets, selected: visibleTargetsSelected, search: targetSearch, setSearch: setTargetSearch, searchLabel: "추가할 설비·구역 검색", allLabel: "대상 전체 선택", empty: "추가할 곳이 없습니다." }].map(section => <Box key={section.key} component="section" aria-label={section.heading} sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "8px", mb: "10px" }}>
                    <Typography component="h3" sx={{ fontSize: 15, fontWeight: 500 }}>{section.heading}</Typography>
                    <Typography sx={{ fontSize: 12, color: "primary.main", whiteSpace: "nowrap" }}><Box component="span" sx={{ fontWeight: 600, fontSize: 14 }}>{selection[section.key].length}</Box>개 선택</Typography>
                  </Box>
                  <TextField fullWidth size="small" placeholder={section.searchLabel} inputProps={{ "aria-label": section.searchLabel }} InputProps={{ startAdornment: searchAdornment }}
                    value={section.search} disabled={busy || !source} onChange={event => section.setSearch(event.target.value)} sx={{ mb: "10px" }} />
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "6px", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 42, px: 1, bgcolor: "action.hover", borderBottom: "1px solid", borderColor: "divider" }}>
                      <FormControlLabel sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12, color: "text.secondary" }, "& .MuiCheckbox-root": { p: 1, "& svg": { fontSize: 20 } } }} label="전체 선택" control={<Checkbox inputProps={{ "aria-label": section.allLabel }} disabled={busy || !section.rows.length}
                        checked={section.rows.length > 0 && section.selected === section.rows.length} indeterminate={section.selected > 0 && section.selected < section.rows.length}
                        onChange={(_, checked) => selectVisible(section.key, section.rows, checked)} />} />
                      <Typography sx={{ fontSize: 12, color: "text.secondary", pr: 1 }}>{section.rows.length}개</Typography>
                    </Box>
                    <Box sx={{ height: listHeight, overflowY: "auto", overflowX: "hidden", overscrollBehavior: "contain", scrollbarGutter: "stable" }}>
                      {section.rows.map(row => <CopyChoice key={row.id} row={row} checked={selection[section.key].includes(row.id)} disabled={busy}
                        onChange={(_, checked) => selectVisible(section.key, [row], checked)} />)}
                      {!section.rows.length && <Box sx={{ height: "100%", display: "grid", placeItems: "center", px: 2 }}><Typography color="text.secondary" sx={{ fontSize: 13, textAlign: "center", ...wrapping }}>{busy ? "불러오는 중" : !source ? "가져올 설비·구역 선택 전" : section.empty}</Typography></Box>}
                    </Box>
                  </Box>
                </Box>)}
              </Box>
              {!snapshot.length && !busy && <Button onClick={load}>다시 불러오기</Button>}
            </> : <>
              <Box sx={{ pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.75 }}>가져올 설비·구역</Typography>
                <Typography sx={{ ...wrapping, fontSize: 15 }}>{source?.label || "선택 없음"}</Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1.2fr) minmax(0, 1fr)" }, gap: "24px" }}>
                <Box sx={{ minWidth: 0 }}><Typography component="h3" sx={{ fontSize: 15, fontWeight: 500, mb: 1 }}>{itemName} {plan?.items.length}개</Typography>
                  {plan?.items.map(item => <Typography key={item.id} sx={{ ...wrapping, py: 1.5, fontSize: 14, borderBottom: "1px solid", borderColor: "divider" }}>{item.label}</Typography>)}
                </Box>
                <Box sx={{ minWidth: 0 }}><Typography component="h3" sx={{ fontSize: 15, fontWeight: 500, mb: 1 }}>받을 설비·구역 {plan?.rows.length}곳</Typography>
                  {plan?.rows.map(row => <Box key={row.target.id} sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography sx={{ ...wrapping, fontSize: 14 }}>{row.target.title || row.target.label}</Typography>
                    {row.target.detail && <Typography sx={{ fontSize: 12, color: "text.secondary", ...wrapping }}>{row.target.detail}</Typography>}
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75 }}>새로 추가 {row.items.length}건 · 이미 등록됨 {row.skipped}건</Typography>
                  </Box>)}
                </Box>
              </Box>
            </>}
          </>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ display: "flex", flexWrap: "wrap", gap: "12px", px: { xs: "16px", sm: "24px" }, py: "16px", borderTop: "1px solid", borderColor: "divider", "& > :not(style) ~ :not(style)": { ml: 0 } }}>
        {phase !== "done" && <Box role="status" sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", ...wrapping }}>{itemName} {selection.itemIds.length}개 · 받을 곳 {selection.targetIds.length}곳</Typography>
          {phase === "preview" && <Typography sx={{ fontSize: 13, color: "text.primary", mt: 0.5 }}>새로 추가 {plan?.added || 0}건 · 이미 등록됨 {plan?.skipped || 0}건</Typography>}
        </Box>}
        <Box sx={{ display: { xs: "grid", sm: "flex" }, gridTemplateColumns: phase === "preview" ? "1fr 1fr" : "auto minmax(0, 1fr)", alignItems: "center", justifyContent: "flex-end", gap: "8px", ml: "auto", minWidth: 0, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
          <Button disabled={busy} color="inherit" onClick={() => setOpen(false)}>{phase === "done" ? "닫기" : "취소"}</Button>
          {phase === "preview" && <Button variant="outlined" disabled={busy} onClick={() => { setPhase("select"); setError(""); }}>선택 수정</Button>}
          {phase === "select" && <Button variant="contained" endIcon={<ArrowForwardIcon />} disabled={busy || !plan} onClick={() => setPhase("preview")}>추가할 내용 확인</Button>}
          {phase === "preview" && <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={busy || !plan} sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
            onClick={confirm}>{plan?.added ? `${plan.added}건 복사하기` : "추가 없이 마치기"}</Button>}
        </Box>
      </DialogActions>
    </Dialog>
  </>;
}
